import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MediaHeader from '../components/MediaItem/MediaHeader';
import MediaPoster from '../components/MediaItem/MediaPoster';
import MediaInfo from '../components/MediaItem/MediaInfo';
import MediaGenres from '../components/MediaItem/MediaGenres';
import MediaMeta from '../components/MediaItem/MediaMeta';
import MediaExtra from '../components/MediaItem/MediaExtra';
import MediaSeasons from '../components/MediaItem/MediaSeasons';
import MediaAdminControls from '../components/MediaItem/MediaAdminControls';
import MediaSection from '../components/MediaSection';
import QuickEditDetails from '../components/MediaItem/QuickEditDetails';
import Modal from '../components/Modal'; // Import your Modal component
import debounce from 'lodash/debounce';
import '../styles/MediaItemPage.css';

const MediaItemPage = ({ apiBaseUrl }) => {
  const { mediaId } = useParams();
  const navigate = useNavigate();
  const { userRoles } = useAuth();
  const canEdit = userRoles.includes('manage-media');
  const isAdmin = userRoles.includes('admin');

  const [mediaItem, setMediaItem] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editedMedia, setEditedMedia] = useState(null);
  const [uploading, setUploading] = useState({ horiz: false, vert: false });
  const [gradient, setGradient] = useState('linear-gradient(135deg,rgb(58, 70, 90), rgba(0, 0, 0, 0.3))');
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal
  const [backgroundModalOpen, setBackgroundModalOpen] = useState(false);
  const [messageModal, setMessageModal] = useState({ isOpen: false, title: '', message: '', icon: null });

  const [similarItems, setSimilarItems] = useState([]);


  const bannerRef = useRef(null);

  // Extract dominant color from banner image for gradient
  useEffect(() => {
    if (mediaItem?.horizontal_image && bannerRef.current) {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = mediaItem.horizontal_image;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const pixel = ctx.getImageData(0, 0, 1, 1).data;
        const rgba = `rgb(${pixel[0]}, ${pixel[1]}, ${pixel[2]}, 0.2)`;
        setGradient(`linear-gradient(135deg, ${rgba}, rgba(40, 76, 147, 0))`);
      };
    }
  }, [mediaItem?.horizontal_image]);

  // Fetch media item and achievements
  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${apiBaseUrl}/media/${mediaId}`, { credentials: 'include' })
        .then((res) => res.json()),
      fetch(`${apiBaseUrl}/achievements/${mediaId}`, { credentials: 'include' })
        .then((res) => res.json())
        .catch(() => []),
    ])
      .then(([mediaData, achievementsData]) => {
        setMediaItem(mediaData);
        setEditedMedia(mediaData);
        setAchievements(achievementsData);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setLoading(false);
      });

      // Fetch similar media items
      fetch(`${apiBaseUrl}/media?filter=similar&media_id=${mediaId}&limit=25`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => setSimilarItems(data)) // <-- use a new state setter here
        .catch(err => console.error('Error fetching similar items:', err));
  }, [apiBaseUrl, mediaId]);

  const toggleEditMode = () => {
    if (isAdmin || canEdit) {
      setEditMode((prev) => !prev);
    }
  };

  const handleSetBackground = () => {
    setBackgroundModalOpen(true);
  };

  const confirmSetBackground = () => {
    if (!mediaItem.image) {
      showMessageModal('Error', 'No poster image available for this media item.', null);
      setBackgroundModalOpen(false);
      return;
    }
    const storedSettings = localStorage.getItem('backgroundSettings');
    const currentSettings = storedSettings
      ? JSON.parse(storedSettings)
      : { blur: 10, opacity: 0.7, coverType: 'cover' };
    const newSettings = { ...currentSettings, imageUrl: mediaItem.image };
    try {
      localStorage.setItem('backgroundSettings', JSON.stringify(newSettings));
      setBackgroundModalOpen(false);
      showMessageModal('Success', `Background set to "${mediaItem.title}" poster.`, null);
    } catch (err) {
      showMessageModal('Error', 'Failed to set background: ' + err.message, null);
      setBackgroundModalOpen(false);
    }
  };

  const cancelSetBackground = () => {
    setBackgroundModalOpen(false);
  };

  const saveField = useCallback(
    debounce((field, value) => {
      fetch(`${apiBaseUrl}/update_quick_setting/${mediaItem.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ field, value }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setMediaItem((prev) => ({ ...prev, ...data.media }));
          } else {
            showMessageModal(
              'Update Failed',
              'Failed to update field: ' + (data.error || 'Unknown error'),
              null
            );
          }
        })
        .catch((err) => {
          showMessageModal(
            'Update Failed',
            'Something went wrong: ' + err.message,
            null
          );
        });
    }, 500),
    [apiBaseUrl, mediaItem]
  );

  const handleInputChange = (field, value) => {
    setEditedMedia((prev) => ({ ...prev, [field]: value }));
    saveField(field, value);
  };

  const handleIncrement = (field) => {
    const newValue = parseInt(editedMedia[field] || 0, 10) + 1;
    setEditedMedia((prev) => ({ ...prev, [field]: newValue }));
    saveField(field, newValue);
  };

  const handleImageUpload = (field, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('media_id', mediaItem.id);
    formData.append('field', field);
    setUploading((prev) => ({
      ...prev,
      [field === 'horizontal_image' ? 'horiz' : 'vert']: true,
    }));

    fetch(`${apiBaseUrl}/upload_image`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.imagePath) {
          setMediaItem((prev) => ({ ...prev, [field]: data.imagePath }));
          setEditedMedia((prev) => ({ ...prev, [field]: data.imagePath }));
          saveField(field, data.imagePath);
        } else {
          showMessageModal(
            'Upload Failed',
            'Failed to upload image: ' + (data.error || 'Unknown error'),
            null
          );
        }
      })
      .catch((err) => {
        showMessageModal(
          'Upload Failed',
          'Something went wrong: ' + err.message,
          null
        );
      })
      .finally(() => {
        setUploading((prev) => ({
          ...prev,
          [field === 'horizontal_image' ? 'horiz' : 'vert']: false,
        }));
      });
  };

  const handleSeasonUpdate = (updatedSeason) => {
    setMediaItem((prev) => ({
      ...prev,
      seasons: prev.seasons.map((s) => (s.id === updatedSeason.id ? updatedSeason : s)),
    }));
  };

  const handleRefresh = () => {
    setIsModalOpen(true); // Open the confirmation modal
  };

  const showMessageModal = (title, message, icon = null) => {
    setMessageModal({ isOpen: true, title, message, icon });
  };
  

  const confirmRefresh = () => {
    fetch(`${apiBaseUrl}/refresh_media/${mediaId}`, {
      method: 'POST',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setMediaItem(data.media); // Update the media item with refreshed data
          setEditedMedia(data.media);
          showMessageModal(
            'Refreshed!',
            'Media item refreshed successfully!',
            null 
          );
        } else {
          showMessageModal(
            'Refresh Failed',
            'Failed to refresh media item: ' + (data.error || 'Unknown error'),
            null
          );
        }
      })
      .catch((err) => {
        showMessageModal(
          'Refresh Failed',
          'Something went wrong: ' + err.message,
          null
        );
      });
  };

  const cancelRefresh = () => {
    setIsModalOpen(false); // Close the modal without refreshing
  };

  if (loading) {
    return <div className="media-item-page"><p>Loading media item…</p></div>;
  }
  if (!mediaItem) {
    return <div className="media-item-page"><p>Error loading media details.</p></div>;
  }

  return (
    <div className="media-item-page" style={{ background: gradient }}>
      <div className="content-wrapper">
        <div className="blurred-bg" style={{ backgroundImage: `url('${mediaItem.horizontal_image}')` }}></div>
        <MediaHeader
          mediaItem={mediaItem}
          editedMedia={editedMedia}
          editMode={editMode}
          isAdmin={isAdmin}
          bannerRef={bannerRef}
          handleInputChange={handleInputChange}
          navigate={navigate}
          canEdit={canEdit}
        />
        <main className="media-details">
          <div className="poster-and-quick-edit">
            <MediaPoster
              mediaItem={mediaItem}
              editMode={editMode}
              isAdmin={isAdmin}
              uploading={uploading}
            />
            <QuickEditDetails
              mediaItem={mediaItem}
              editedMedia={editedMedia}
              editMode={editMode}
              canEdit={canEdit}
              handleInputChange={handleInputChange}
              handleIncrement={handleIncrement}
              apiBaseUrl={apiBaseUrl}
            />
          </div>
          <div className="info-section info-section-cards">
            <MediaInfo
              mediaItem={mediaItem}
              editedMedia={editedMedia}
              editMode={editMode}
              isAdmin={isAdmin}
              handleInputChange={handleInputChange}
            />
            <MediaGenres genres={mediaItem.genres} />
            <MediaMeta
              mediaItem={mediaItem}
              editedMedia={editedMedia}
              editMode={editMode}
              isAdmin={isAdmin}
              handleInputChange={handleInputChange}
            />
          </div>
        </main>
        {/* <MediaSeasons
          seasons={mediaItem.seasons}
          editMode={editMode}
          isAdmin={isAdmin}
          canEdit={canEdit}
          apiBaseUrl={apiBaseUrl}
          onUpdate={handleSeasonUpdate}
        /> */}
        <MediaAdminControls
          editMode={editMode}
          isAdmin={isAdmin}
          canEdit={canEdit}
          toggleEditMode={toggleEditMode}
          onRefresh={handleRefresh}
          onSetBackground={handleSetBackground}
        />
        <Modal
          isOpen={isModalOpen}
          onClose={cancelRefresh}
          title="Confirm Refresh"
          message="Are you sure you want to refresh this media item? This will overwrite all API-derived data with the latest information, but preserve your personal data (e.g., status, notes)."
          buttons={[
            { label: 'Cancel', class: 'btn btn-cancel', action: () => {} }, // Empty action since onClose handles it
            { label: 'Confirm', class: 'btn btn-confirm', action: confirmRefresh },
          ]}
        />
         <Modal
          isOpen={messageModal.isOpen}
          onClose={() => setMessageModal({ ...messageModal, isOpen: false })}
          title={messageModal.title}
          message={messageModal.message}
          icon={messageModal.icon}
          buttons={[
            { label: 'OK', class: 'btn btn-confirm', action: () => {} },
          ]}
        />
        <Modal
          isOpen={backgroundModalOpen}
          onClose={cancelSetBackground}
          title="Set as Background"
          message={`Set "${mediaItem.title}" poster as the background image?`}
          buttons={[
            { label: 'Cancel', class: 'btn btn-cancel', action: () => {} },
            { label: 'Confirm', class: 'btn btn-confirm', action: confirmSetBackground },
          ]}
        />
      </div>
      <MediaExtra
          mediaItem={mediaItem}
          editedMedia={editedMedia}
          editMode={editMode}
          isAdmin={isAdmin}
          canEdit={canEdit}
          achievements={achievements}
          handleInputChange={handleInputChange}
          handleIncrement={handleIncrement}
        />
      <div style={{maxWidth:"1350px", margin:"auto"}}>
       <MediaSection title="Also check out:" items={similarItems} />
      </div>

    </div>
    
  );
};

export default MediaItemPage;